import { Component, OnDestroy, OnInit } from "@angular/core";
import { ControlContainer, NgModelGroup } from "@angular/forms";
import { MetadataInputSelectComponent } from "./metadata-input-select.component";
import { MetadataKey } from "../../../../interfaces";
import { KeyValuePair } from "@tylertech/tyler-api-types";
import { Subject } from "rxjs";
import { debounceTime, takeUntil } from "rxjs/operators";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

@Component({
    selector: "metadata-input-dual-list",
    templateUrl: "./metadata-input-dual-list.tmpl.html",
    viewProviders: [{ provide: ControlContainer, useExisting: NgModelGroup }],
    standalone: false
})
export class MetadataInputDualListComponent extends MetadataInputSelectComponent implements OnInit, OnDestroy
{
    private sortUpdateSubject$ = new Subject<void>();
    private readonly destroy$ = new Subject<void>();

    ngOnInit(): void
    {
        this.sortUpdateSubject$.pipe(debounceTime(1000), takeUntil(this.destroy$))
            .subscribe(() => this.fireValueChanged(this.selected));
        super.ngOnInit();
    }

    ngOnDestroy(): void
    {
        this.destroy$.next();
        this.destroy$.complete();
        this.sortUpdateSubject$.complete();
        super.ngOnDestroy();
    }

    onChange(): void
    {
        this.loadMoreCount = 1;
    }

    sort(descending: boolean = false): void
    {
        const list: KeyValuePair<MetadataKey, string>[] = [];
        list.push(...this._options);
        list.sort((a, b) => this.compare(a, b, descending)).forEach((x, i) =>
        {
            this._options.find(o => o.Key === x.Key)["$sortedIndex"] = i;
        });
        this.updateAfterSort(false);
    }

    compare(a: KeyValuePair<MetadataKey, string>, b: KeyValuePair<MetadataKey, string>, descending: boolean): number
    {
        const aVal = a.Value.toLowerCase();
        const bVal = b.Value.toLowerCase();
        if (descending)
            return aVal > bVal ? -1 : 1;
        else
            return aVal < bVal ? -1 : 1;
    }

    moveUp(): void
    {
        const currentSelectedItem = this.deselectList.find(x => x.Key === this.deselectListSelections[0]);
        const currentSortedIndex = currentSelectedItem["$sortedIndex"];
        const indexOfAbove = this.deselectList.indexOf(currentSelectedItem) - 1;
        const aboveSortedIndex = this.deselectList[indexOfAbove]["$sortedIndex"];
        this._options.forEach(x =>
        {
            if (x["$sortedIndex"] >= aboveSortedIndex && x["$sortedIndex"] < currentSortedIndex)
                x["$sortedIndex"] = x["$sortedIndex"] + 1;
            else if (x["$sortedIndex"] === currentSortedIndex)
                x["$sortedIndex"] = aboveSortedIndex;
        });
        this.updateAfterSort(true);
    }

    moveDown(): void
    {
        const currentSelectedItem = this.deselectList.find(x => x.Key === this.deselectListSelections[0]);
        const currentSortedIndex = currentSelectedItem["$sortedIndex"];
        const indexOfBelow = this.deselectList.indexOf(currentSelectedItem) + 1;
        const belowSortedIndex = this.deselectList[indexOfBelow]["$sortedIndex"];
        this._options.forEach(x =>
        {
            if (x["$sortedIndex"] <= belowSortedIndex && x["$sortedIndex"] > currentSortedIndex)
                x["$sortedIndex"] = x["$sortedIndex"] - 1;
            else if (x["$sortedIndex"] === currentSortedIndex)
                x["$sortedIndex"] = belowSortedIndex;
        });
        this.updateAfterSort(true);
    }

    moveToTop(): void
    {
        const currentSelectedItem = this.deselectList.find(x => x.Key === this.deselectListSelections[0]);
        const currentSortedIndex = currentSelectedItem["$sortedIndex"];
        this._options.forEach(x =>
        {
            if (x["$sortedIndex"] < currentSortedIndex)
                x["$sortedIndex"] = x["$sortedIndex"] + 1;
            else if (x["$sortedIndex"] === currentSortedIndex)
                x["$sortedIndex"] = 0;
        });
        this.updateAfterSort(false);
    }

    moveToBottom(): void
    {
        const currentSelectedItem = this.deselectList.find(x => x.Key === this.deselectListSelections[0]);
        const currentSortedIndex = currentSelectedItem["$sortedIndex"];
        this._options.forEach(x =>
        {
            if (x["$sortedIndex"] > currentSortedIndex)
                x["$sortedIndex"] = x["$sortedIndex"] - 1;
            else if (x["$sortedIndex"] === currentSortedIndex)
                x["$sortedIndex"] = this._options.length - 1;
        });
        this.updateAfterSort(false);
    }

    private updateAfterSort(debounce: boolean): void
    {
        this.propertyMetadata["$sorted"] = true;
        this.selected = this._deselectList.map(x => x.Key);
        if (debounce)
            this.sortUpdateSubject$.next();
        else
            this.fireValueChanged(this.selected);
    }

    get moveUpDisabled(): boolean
    {
        return this.evaluateDisabled(0);
    }

    get moveDownDisabled(): boolean
    {
        return this.evaluateDisabled(this.deselectList.length - 1);
    }

    evaluateDisabled(index: number): boolean
    {
        if (!this.deselectList[0])
            return true;
        if (this.deselectListSelections.length !== 1)
            return true;
        if (this.deselectListSelections[0] === this.deselectList[index].Key)
            return true;
        return false;
    }
}
